/**
 * 坦克大战输入控制器 - 键盘 + 鼠标 + 触控
 * 
 * 触控布局：
 *   左侧: 虚拟摇杆 (Y轴=前进/后退, X轴=左转/右转)
 *   右侧: 炮塔旋转按钮 + 开火按钮
 */
export interface TankInput {
  forward: number;
  turn: number;
  turretTurn: number;
  fire: boolean;
}

export class InputController {
  // 键盘
  private readonly keys = new Set<string>();
  private mouseDown = false;

  // 触控摇杆
  private joystickActive = false;
  private joystickPointerId: number | null = null;
  private joystickX = 0;
  private joystickY = 0;

  // 触控按钮
  private turretLeftDown = false;
  private turretRightDown = false;
  private fireTouchDown = false;

  private disposed = false;
  private readonly joystickArea: HTMLElement | null;
  private readonly joystickThumb: HTMLElement | null;

  // 摇杆基准点
  private joystickCenterX = 0;
  private joystickCenterY = 0;
  private joystickRadius = 0;

  constructor() {
    // 键盘
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', this.onContextMenu);

    // 触控元素查找
    const area = document.getElementById('touch-joystick-area');
    const thumb = document.getElementById('touch-joystick-thumb');
    const turretL = document.getElementById('turret-left');
    const turretR = document.getElementById('turret-right');
    const fireBtn = document.getElementById('fire-button');

    this.joystickArea = area;
    this.joystickThumb = thumb;

    // 始终初始化摇杆事件 (Pointer Event 同时支持鼠标和触控)
    if (area) {
      this.initJoystick(area);
    }

    // 炮塔按钮 (始终初始化, 桌面也可点)
    if (turretL) {
      turretL.addEventListener('pointerdown', (e) => { e.preventDefault(); this.turretLeftDown = true; });
      turretL.addEventListener('pointerup', (e) => { e.preventDefault(); this.turretLeftDown = false; });
      turretL.addEventListener('pointerleave', () => { this.turretLeftDown = false; });
      turretL.addEventListener('pointercancel', () => { this.turretLeftDown = false; });
    }
    if (turretR) {
      turretR.addEventListener('pointerdown', (e) => { e.preventDefault(); this.turretRightDown = true; });
      turretR.addEventListener('pointerup', (e) => { e.preventDefault(); this.turretRightDown = false; });
      turretR.addEventListener('pointerleave', () => { this.turretRightDown = false; });
      turretR.addEventListener('pointercancel', () => { this.turretRightDown = false; });
    }
    if (fireBtn) {
      fireBtn.addEventListener('pointerdown', (e) => { e.preventDefault(); this.fireTouchDown = true; });
      fireBtn.addEventListener('pointerup', (e) => { e.preventDefault(); this.fireTouchDown = false; });
      fireBtn.addEventListener('pointerleave', () => { this.fireTouchDown = false; });
      fireBtn.addEventListener('pointercancel', () => { this.fireTouchDown = false; });
    }
  }

  private initJoystick(area: HTMLElement): void {
    area.addEventListener('pointerdown', this.onJoystickDown);
    area.addEventListener('pointermove', this.onJoystickMove);
    area.addEventListener('pointerup', this.onJoystickUp);
    area.addEventListener('pointercancel', this.onJoystickUp);
  }

  readInput(): TankInput {
    let forward = 0;
    let turn = 0;
    let turretTurn = 0;

    // ===== 键盘输入 =====
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) forward += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) forward -= 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) turn += 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) turn -= 1;
    if (this.keys.has('KeyQ')) turretTurn += 1;
    if (this.keys.has('KeyE')) turretTurn -= 1;

    // ===== 触控摇杆 =====
    if (this.joystickActive) {
      // Y轴映射为前进/后退 (向上=正=前进)
      forward += -this.joystickY;  // 注意: 屏幕Y轴向下为正, 所以取反
      // X轴映射为转向
      turn += -this.joystickX;     // 左=负 → turn+ (左转)
    }

    // ===== 触控炮塔按钮 =====
    if (this.turretLeftDown) turretTurn += 1;
    if (this.turretRightDown) turretTurn -= 1;

    // Clamp
    forward = Math.max(-1, Math.min(1, forward));
    turn = Math.max(-1, Math.min(1, turn));
    turretTurn = Math.max(-1, Math.min(1, turretTurn));

    // ===== 开火 =====
    const fire = this.keys.has('Space') || this.mouseDown || this.fireTouchDown;

    return { forward, turn, turretTurn, fire };
  }

  consumeKey(code: string): boolean {
    if (this.keys.has(code)) {
      this.keys.delete(code);
      return true;
    }
    return false;
  }

  // ===== 键盘/鼠标事件 =====
  private readonly onKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.code);
    if (event.code === 'Space') event.preventDefault();
  };
  private readonly onKeyUp = (event: KeyboardEvent) => { this.keys.delete(event.code); };
  private readonly onMouseDown = (event: MouseEvent) => { if (event.button === 0) this.mouseDown = true; };
  private readonly onMouseUp = (event: MouseEvent) => { if (event.button === 0) this.mouseDown = false; };
  private readonly onContextMenu = (event: Event) => { event.preventDefault(); };

  // ===== 摇杆事件 =====
  private readonly onJoystickDown = (event: PointerEvent) => {
    event.preventDefault();
    const area = this.joystickArea!;
    const rect = area.getBoundingClientRect();
    this.joystickCenterX = rect.left + rect.width / 2;
    this.joystickCenterY = rect.top + rect.height / 2;
    this.joystickRadius = rect.width * 0.42;
    this.joystickActive = true;
    this.joystickPointerId = event.pointerId;
    try { area.setPointerCapture(event.pointerId); } catch { /* test env */ }
    this.updateJoystick(event.clientX, event.clientY);
  };

  private readonly onJoystickMove = (event: PointerEvent) => {
    if (!this.joystickActive || event.pointerId !== this.joystickPointerId) return;
    event.preventDefault();
    this.updateJoystick(event.clientX, event.clientY);
  };

  private readonly onJoystickUp = (event: PointerEvent) => {
    if (event.pointerId !== this.joystickPointerId) return;
    event.preventDefault();
    this.joystickActive = false;
    this.joystickPointerId = null;
    this.joystickX = 0;
    this.joystickY = 0;
    this.updateThumb(0, 0);
  };

  private updateJoystick(clientX: number, clientY: number): void {
    let dx = (clientX - this.joystickCenterX) / this.joystickRadius;
    let dy = (clientY - this.joystickCenterY) / this.joystickRadius;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 1) { dx /= len; dy /= len; }
    this.joystickX = dx;
    this.joystickY = dy;
    this.updateThumb(dx, dy);
  }

  private updateThumb(nx: number, ny: number): void {
    if (!this.joystickThumb) return;
    const dist = 38; // 最大偏移像素
    const tx = nx * dist;
    const ty = ny * dist;
    this.joystickThumb.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`;
    if (this.joystickActive) {
      this.joystickThumb.classList.add('active');
    } else {
      this.joystickThumb.classList.remove('active');
    }
  }

  // ===== 清理 =====
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);

    const area = this.joystickArea;
    if (area) {
      area.removeEventListener('pointerdown', this.onJoystickDown);
      area.removeEventListener('pointermove', this.onJoystickMove);
      area.removeEventListener('pointerup', this.onJoystickUp);
      area.removeEventListener('pointercancel', this.onJoystickUp);
    }
  }
}
